import { useState } from 'react';

interface InlineEditFieldProps {
  /** Field label (e.g. "Name", "Organization"). */
  label: string;
  /** Current persisted value, or null when unset. */
  value: string | null;
  /** Whether the user may edit this field. When false, only the read-only value renders. */
  canEdit: boolean;
  /**
   * Called with the trimmed, validated value when the user saves. If it returns
   * a promise (e.g. a TanStack Query `mutateAsync`), the field leaves edit mode
   * once it resolves and stays in edit mode (showing `serverError`) if it rejects.
   */
  onSave: (trimmedValue: string) => void | Promise<unknown>;
  /** Whether the parent mutation is in flight. */
  isPending: boolean;
  /** Error from the parent mutation, surfaced beneath the input after a save attempt. */
  serverError?: string | null;
  /** Maximum length (inclusive). Defaults to 100. */
  maxLength?: number;
  /** Input placeholder. */
  placeholder?: string;
  /** Label shown (italic, muted) when the value is empty. Defaults to "Not set". */
  notSetLabel?: string;
  /** id used to associate the <label> with the <input> in edit mode. */
  inputId: string;
  /** Accessible name for the Edit button. Defaults to the visible text "Edit". */
  editButtonLabel?: string;
}

export default function InlineEditField({
  label,
  value,
  canEdit,
  onSave,
  isPending,
  serverError,
  maxLength = 100,
  placeholder,
  notSetLabel = 'Not set',
  inputId,
  editButtonLabel,
}: InlineEditFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedValue, setEditedValue] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  // Track whether a save was attempted in the current edit session, so a stale
  // parent error from a previous session never shows on a fresh edit.
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const startEditing = () => {
    setEditedValue(value ?? '');
    setValidationError(null);
    setHasSubmitted(false);
    setIsEditing(true);
  };

  const handleSave = async () => {
    const trimmed = editedValue.trim();
    if (!trimmed) {
      setValidationError(`${label} cannot be empty`);
      return;
    }
    if (trimmed.length > maxLength) {
      setValidationError(`${label} is too long (max ${maxLength} characters)`);
      return;
    }
    setValidationError(null);
    setHasSubmitted(true);
    try {
      await onSave(trimmed);
      // Success: return to read mode.
      setIsEditing(false);
      setEditedValue('');
      setHasSubmitted(false);
    } catch {
      // Failure: stay in edit mode; the error surfaces via `serverError`.
    }
  };

  const handleCancel = () => {
    setEditedValue('');
    setValidationError(null);
    setHasSubmitted(false);
    setIsEditing(false);
  };

  // Local validation takes precedence; otherwise surface the parent mutation
  // error, but only after a save attempt in this edit session.
  const errorMessage = validationError ?? (hasSubmitted ? serverError : null);

  // Only render the htmlFor/id association in edit mode, where the input exists.
  if (canEdit && isEditing) {
    return (
      <div>
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-gray-500 mb-1"
        >
          {label}
        </label>
        <div className="space-y-2">
          <div className="flex gap-2">
            <input
              id={inputId}
              type="text"
              value={editedValue}
              onChange={(e) => setEditedValue(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              maxLength={maxLength}
              autoFocus
              placeholder={placeholder}
            />
            <button
              onClick={handleSave}
              disabled={isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 font-medium transition-colors"
            >
              {isPending ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={handleCancel}
              disabled={isPending}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 font-medium transition-colors"
            >
              Cancel
            </button>
          </div>
          {errorMessage && <p className="text-sm text-red-600">{errorMessage}</p>}
        </div>
      </div>
    );
  }

  // Read mode: no <label htmlFor> (no input exists to point at).
  return (
    <div>
      <p className="block text-sm font-medium text-gray-500 mb-1">{label}</p>
      <div className="flex items-center justify-between">
        <p className="text-gray-900">
          {value || <span className="text-gray-400 italic">{notSetLabel}</span>}
        </p>
        {canEdit && (
          <button
            aria-label={editButtonLabel}
            onClick={startEditing}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            Edit
          </button>
        )}
      </div>
    </div>
  );
}
